/**
 * Pestaña "Rotos" — registro diario de huevos rotos por tipo y por lote.
 *
 * Cada entrada genera un movimiento "Rotura" y descuenta del inventario.
 * Permite seleccionar el lote específico del cual se descuenta (o Auto FIFO).
 * Permite editar / eliminar para corregir errores (revierte el descuento).
 */
import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../components/ui/table';
import { Badge } from '../../components/ui/badge';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '../../components/ui/alert-dialog';
import { Pencil, Trash2, Plus, X, AlertCircle, EggOff, Calendar, Package, Layers } from 'lucide-react';
import { toast } from 'sonner';

import { getRotos, createRoto, updateRoto, deleteRoto, getInventarioPorLote } from '../../lib/api';
import { TIPOS_CONTEO, TIPO_LABELS_CONTEO } from './constants';
import { formatDate } from '../../lib/dateUtils';

const todayISO = () => new Date().toISOString().split('T')[0];

export const RotosTab = ({ onAfterChange }) => {
  const [rotos, setRotos] = useState([]);
  const [loading, setLoading] = useState(false);

  // Filtros (persistidos en sesión)
  const [filtroDesde, setFiltroDesde] = useState('');
  const [filtroHasta, setFiltroHasta] = useState('');
  const [filtroTipo, setFiltroTipo] = useState('all');

  // Form para registrar nuevo o editar
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState({
    fecha: todayISO(),
    tipo_huevo: 'grande',
    cantidad: '',
    observaciones: '',
    lote_id: '', // Debe elegirse explícitamente
  });
  const [saving, setSaving] = useState(false);

  // Lotes disponibles para el tipo seleccionado
  const [lotesDisponibles, setLotesDisponibles] = useState([]);
  const [loadingLotes, setLoadingLotes] = useState(false);

  const [deletingId, setDeletingId] = useState(null);

  const buildParams = useCallback(() => {
    const p = {};
    if (filtroDesde) p.fecha_desde = filtroDesde;
    if (filtroHasta) p.fecha_hasta = filtroHasta;
    if (filtroTipo && filtroTipo !== 'all') p.tipo_huevo = filtroTipo;
    return p;
  }, [filtroDesde, filtroHasta, filtroTipo]);

  const loadRotos = useCallback(async () => {
    try {
      setLoading(true);
      const r = await getRotos(buildParams());
      setRotos(r.data || []);
    } catch (err) {
      console.error('Error cargando rotos:', err);
      toast.error('Error cargando rotos');
    } finally {
      setLoading(false);
    }
  }, [buildParams]);

  useEffect(() => { loadRotos(); }, [loadRotos]);

  // Cargar lotes disponibles para el tipo seleccionado (o al abrir edición)
  const loadLotes = useCallback(async (tipoHuevo) => {
    if (!tipoHuevo) return [];
    try {
      setLoadingLotes(true);
      const r = await getInventarioPorLote({ tipo: tipoHuevo });
      const arr = (r.data?.lotes || []).map((l) => ({
        lote_id: l.lote_id,
        lote_codigo: l.lote_codigo,
        disponible: Number(l.disponible || 0),
      }));
      setLotesDisponibles(arr);
      return arr;
    } catch (err) {
      console.error('Error cargando lotes:', err);
      setLotesDisponibles([]);
      return [];
    } finally {
      setLoadingLotes(false);
    }
  }, []);

  useEffect(() => {
    (async () => {
      const arr = await loadLotes(form.tipo_huevo);
      // Si el lote actual ya no aparece o no tiene stock, seleccionar el primero con stock
      const primeroConStock = arr.find((l) => (l.disponible || 0) > 0);
      const actualValido = form.lote_id && arr.some((l) => l.lote_id === form.lote_id);
      if (!actualValido) {
        setForm((f) => ({ ...f, lote_id: primeroConStock ? primeroConStock.lote_id : '' }));
      }
    })();
  }, [form.tipo_huevo, loadLotes]);

  const limpiarFiltros = () => {
    setFiltroDesde('');
    setFiltroHasta('');
    setFiltroTipo('all');
  };

  const resetForm = () => {
    setEditingId(null);
    setForm({
      fecha: todayISO(),
      tipo_huevo: 'grande',
      cantidad: '',
      observaciones: '',
      lote_id: '', // el useEffect elegirá el primero con stock
    });
  };

  const handleEdit = (r) => {
    setEditingId(r.id);
    setForm({
      fecha: r.fecha?.slice(0, 10) || todayISO(),
      tipo_huevo: r.tipo_huevo,
      cantidad: String(r.cantidad || ''),
      observaciones: r.observaciones || '',
      lote_id: r.lote_id || '',
    });
    // scroll al formulario
    try {
      document.getElementById('roto-form-card')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    } catch (_) { /* noop */ }
  };

  // Lote seleccionado (objeto completo) — para mostrar stock disponible.
  const loteSeleccionado = useMemo(() => {
    if (!form.lote_id) return null;
    return lotesDisponibles.find((l) => l.lote_id === form.lote_id) || null;
  }, [form.lote_id, lotesDisponibles]);

  // Solo lotes con stock > 0 (los que se muestran en el dropdown)
  const lotesConStock = useMemo(
    () => lotesDisponibles.filter((l) => (l.disponible || 0) > 0),
    [lotesDisponibles],
  );

  // Stock máximo disponible para el lote seleccionado (para validación cliente).
  const stockMaxDisponible = loteSeleccionado?.disponible ?? 0;

  const handleSave = async () => {
    const cant = parseInt(form.cantidad, 10) || 0;
    if (!form.fecha) { toast.error('Seleccione una fecha'); return; }
    if (!form.tipo_huevo) { toast.error('Seleccione el tipo de huevo'); return; }
    if (!form.lote_id) { toast.error('Debes seleccionar el lote del cual se rompieron los huevos'); return; }
    if (cant <= 0) { toast.error('La cantidad debe ser mayor a 0'); return; }

    // Validación cliente: stock disponible del lote elegido
    if (cant > stockMaxDisponible) {
      toast.error(
        `Cantidad excede el stock disponible en el lote ${loteSeleccionado?.lote_codigo || ''} (${stockMaxDisponible.toLocaleString('en-US')} ${TIPO_LABELS_CONTEO[form.tipo_huevo] || form.tipo_huevo})`
      );
      return;
    }

    setSaving(true);
    try {
      const payload = {
        fecha: form.fecha,
        tipo_huevo: form.tipo_huevo,
        cantidad: cant,
        observaciones: form.observaciones || null,
        lote_id: form.lote_id,
      };
      if (editingId) {
        await updateRoto(editingId, payload);
        toast.success('Roto actualizado · inventario re-ajustado');
      } else {
        await createRoto(payload);
        const codigo = loteSeleccionado?.lote_codigo || '';
        const loteLabel = codigo.toLowerCase().startsWith('lote') ? codigo : `Lote ${codigo}`;
        toast.success(`${cant.toLocaleString('en-US')} ${form.tipo_huevo} descontados del ${loteLabel}`);
      }
      resetForm();
      await loadRotos();
      // Recargar lotes disponibles para reflejar el nuevo stock
      await loadLotes(form.tipo_huevo);
      if (typeof onAfterChange === 'function') onAfterChange();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error guardando roto');
    } finally {
      setSaving(false);
    }
  };

  const handleEnter = (e) => {
    if (e.key !== 'Enter') return;
    if (e.target.tagName !== 'INPUT') return;
    e.preventDefault();
    const root = e.currentTarget;
    const inputs = Array.from(
      root.querySelectorAll('input:not([disabled])'),
    ).filter((el) => el.offsetParent !== null);
    const idx = inputs.indexOf(e.target);
    if (idx === -1) return;
    if (idx < inputs.length - 1) {
      const nxt = inputs[idx + 1];
      nxt.focus();
      try { if (nxt.select) nxt.select(); } catch (_) { /* noop */ }
    } else if (!saving) {
      handleSave();
    }
  };

  const confirmDelete = async () => {
    if (!deletingId) return;
    try {
      await deleteRoto(deletingId);
      toast.success('Roto eliminado · inventario revertido');
      setDeletingId(null);
      await loadRotos();
      await loadLotes(form.tipo_huevo);
      if (typeof onAfterChange === 'function') onAfterChange();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error eliminando roto');
    }
  };

  // Totales agrupados por tipo de huevo (en el rango filtrado)
  const totalesPorTipo = useMemo(() => {
    const acc = {};
    for (const r of rotos) {
      acc[r.tipo_huevo] = (acc[r.tipo_huevo] || 0) + (r.cantidad || 0);
    }
    return acc;
  }, [rotos]);

  const totalGeneral = useMemo(
    () => rotos.reduce((s, r) => s + (r.cantidad || 0), 0),
    [rotos],
  );

  const deletingRoto = rotos.find((r) => r.id === deletingId);

  return (
    <div className="space-y-4">
      {/* Card del formulario */}
      <Card id="roto-form-card">
        <CardHeader className="pb-3">
          <CardTitle className="flex items-center gap-2 text-base" style={{ fontFamily: 'Space Grotesk' }}>
            <EggOff className="w-5 h-5 text-destructive" />
            {editingId ? 'Editar Roto' : 'Registrar Rotura del Día'}
          </CardTitle>
          <p className="text-xs text-muted-foreground">
            Cada registro descuenta los huevos del inventario disponible y queda
            como movimiento &quot;Rotura&quot; en el historial. <strong>Selecciona el lote</strong>{' '}
            del cual salieron los rotos para que solo afecte a ese lote.
          </p>
        </CardHeader>
        <CardContent>
          <div
            className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-6 gap-3 items-start"
            onKeyDown={handleEnter}
          >
            <div>
              <Label htmlFor="roto-fecha" className="text-xs h-4 block">Fecha *</Label>
              <Input
                id="roto-fecha"
                type="date"
                value={form.fecha}
                onChange={(e) => setForm({ ...form, fecha: e.target.value })}
                className="h-10 mt-1"
                data-testid="roto-fecha"
              />
            </div>
            <div>
              <Label className="text-xs h-4 block">Tipo de Huevo *</Label>
              <Select
                value={form.tipo_huevo}
                onValueChange={(v) => setForm({ ...form, tipo_huevo: v, lote_id: '' })}
              >
                <SelectTrigger className="h-10 mt-1" data-testid="roto-tipo">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {TIPOS_CONTEO.map((t) => (
                    <SelectItem key={t} value={t}>
                      {TIPO_LABELS_CONTEO[t]}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="lg:col-span-2 relative">
              <Label className="text-xs h-4 block">Lote *</Label>
              <Select
                value={form.lote_id}
                onValueChange={(v) => setForm({ ...form, lote_id: v })}
                disabled={loadingLotes || lotesConStock.length === 0}
              >
                <SelectTrigger className="h-10 mt-1" data-testid="roto-lote">
                  <SelectValue
                    placeholder={
                      loadingLotes
                        ? 'Cargando lotes…'
                        : lotesConStock.length === 0
                          ? 'Sin lotes con stock disponible'
                          : 'Seleccionar lote *'
                    }
                  />
                </SelectTrigger>
                <SelectContent>
                  {lotesConStock.map((l) => {
                    const codigo = l.lote_codigo || '';
                    const label = codigo.toLowerCase().startsWith('lote') ? codigo : `Lote ${codigo}`;
                    return (
                      <SelectItem key={l.lote_id} value={l.lote_id} data-testid={`roto-lote-${codigo}`}>
                        {label}
                      </SelectItem>
                    );
                  })}
                  {!loadingLotes && lotesConStock.length === 0 && (
                    <SelectItem value="__none__" disabled>
                      No hay lotes con stock disponible
                    </SelectItem>
                  )}
                </SelectContent>
              </Select>
              {!loadingLotes && !loteSeleccionado && (
                <p
                  className={`absolute left-0 top-full mt-1 text-[11px] whitespace-nowrap ${
                    lotesConStock.length === 0 ? 'text-muted-foreground' : 'text-destructive'
                  }`}
                >
                  {lotesConStock.length === 0
                    ? `No hay ${TIPO_LABELS_CONTEO[form.tipo_huevo]?.toLowerCase() || 'huevos'} con stock en ningún lote`
                    : 'Selecciona un lote antes de registrar la rotura'}
                </p>
              )}
            </div>
            <div>
              <Label htmlFor="roto-cant" className="text-xs h-4 block">Cantidad rota *</Label>
              <Input
                id="roto-cant"
                type="number"
                inputMode="numeric"
                min="1"
                placeholder="0"
                value={form.cantidad}
                onChange={(e) => setForm({ ...form, cantidad: e.target.value })}
                className="h-10 mt-1 tabular-nums"
                data-testid="roto-cantidad"
              />
            </div>
            <div className="flex flex-col">
              <Label className="text-xs h-4 block invisible" aria-hidden="true">Acciones</Label>
              <div className="flex items-center gap-2 mt-1">
                <Button
                  onClick={handleSave}
                  disabled={saving}
                  className="flex-1 h-10"
                  data-testid="btn-guardar-roto"
                >
                  {saving
                    ? 'Guardando…'
                    : (editingId ? 'Actualizar' : <><Plus className="w-4 h-4 mr-1" /> Registrar</>)}
                </Button>
                {editingId && (
                  <Button
                    variant="outline"
                    onClick={resetForm}
                    disabled={saving}
                    className="h-10 shrink-0"
                    title="Cancelar edición"
                    data-testid="btn-cancel-roto"
                  >
                    <X className="w-4 h-4" />
                  </Button>
                )}
              </div>
            </div>
            <div className="sm:col-span-2 lg:col-span-6">
              <Label htmlFor="roto-obs" className="text-xs h-4 block">Observaciones</Label>
              <Input
                id="roto-obs"
                type="text"
                placeholder="Ej. caída en empaque, transporte, etc."
                value={form.observaciones}
                onChange={(e) => setForm({ ...form, observaciones: e.target.value })}
                className="h-10 mt-1"
                data-testid="roto-obs"
              />
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Card de totales rápidos */}
      {rotos.length > 0 && (
        <Card>
          <CardContent className="py-3">
            <div className="flex flex-wrap items-center gap-2">
              <Badge variant="outline" className="text-xs" data-testid="rotos-total-general">
                <Package className="w-3 h-3 mr-1" />
                Total general: <strong className="ml-1 tabular-nums">{totalGeneral.toLocaleString('en-US')}</strong>
              </Badge>
              {Object.entries(totalesPorTipo).map(([tipo, n]) => (
                <Badge key={tipo} variant="secondary" className="text-xs tabular-nums">
                  {TIPO_LABELS_CONTEO[tipo] || tipo}: <strong className="ml-1">{n.toLocaleString('en-US')}</strong>
                </Badge>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Card del listado */}
      <Card>
        <CardHeader className="pb-3">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
            <CardTitle className="flex items-center gap-2 text-base" style={{ fontFamily: 'Space Grotesk' }}>
              <Calendar className="w-5 h-5" />
              Historial de Roturas
            </CardTitle>
            <div className="flex flex-wrap items-end gap-2">
              <Input
                type="date"
                placeholder="Desde"
                value={filtroDesde}
                onChange={(e) => setFiltroDesde(e.target.value)}
                className="w-[140px] h-9"
                data-testid="rotos-filtro-desde"
              />
              <Input
                type="date"
                placeholder="Hasta"
                value={filtroHasta}
                onChange={(e) => setFiltroHasta(e.target.value)}
                className="w-[140px] h-9"
                data-testid="rotos-filtro-hasta"
              />
              <Select value={filtroTipo} onValueChange={setFiltroTipo}>
                <SelectTrigger className="w-[140px] h-9">
                  <SelectValue placeholder="Tipo" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Todos</SelectItem>
                  {TIPOS_CONTEO.map((t) => (
                    <SelectItem key={t} value={t}>{TIPO_LABELS_CONTEO[t]}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Button
                variant="ghost"
                size="sm"
                onClick={limpiarFiltros}
                data-testid="rotos-filtro-limpiar"
              >
                Limpiar
              </Button>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="text-center py-8 text-muted-foreground text-sm">Cargando…</div>
          ) : rotos.length === 0 ? (
            <div className="text-center py-10 text-muted-foreground">
              <EggOff className="w-12 h-12 mx-auto mb-3 opacity-30" />
              <p>No hay roturas registradas</p>
              <p className="text-xs mt-1">Usa el formulario de arriba para descontar huevos rotos del inventario.</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Fecha</TableHead>
                    <TableHead>Tipo</TableHead>
                    <TableHead>Lote</TableHead>
                    <TableHead className="text-right">Cantidad</TableHead>
                    <TableHead>Observaciones</TableHead>
                    <TableHead className="text-right w-[110px]">Acciones</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {rotos.map((r) => (
                    <TableRow key={r.id} className="hover:bg-accent/60 transition-colors" data-testid={`roto-row-${r.id}`}>
                      <TableCell className="font-medium">{formatDate(r.fecha)}</TableCell>
                      <TableCell>
                        <Badge variant="outline" className="text-xs">
                          {TIPO_LABELS_CONTEO[r.tipo_huevo] || r.tipo_huevo}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        {r.lote_codigo ? (
                          <Badge variant="secondary" className="text-xs" data-testid={`roto-lote-cell-${r.id}`}>
                            <Layers className="w-3 h-3 mr-1" />
                            {r.lote_codigo.toLowerCase().startsWith('lote') ? r.lote_codigo : `Lote ${r.lote_codigo}`}
                          </Badge>
                        ) : (
                          <span className="text-xs text-muted-foreground italic">FIFO / sin lote</span>
                        )}
                      </TableCell>
                      <TableCell className="text-right tabular-nums">
                        <Badge variant="destructive" className="text-xs tabular-nums">
                          {(r.cantidad || 0).toLocaleString('en-US')}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-xs text-muted-foreground max-w-[280px] truncate" title={r.observaciones || ''}>
                        {r.observaciones || '—'}
                      </TableCell>
                      <TableCell className="text-right">
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => handleEdit(r)}
                          title="Editar"
                          data-testid={`btn-edit-roto-${r.id}`}
                        >
                          <Pencil className="w-4 h-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => setDeletingId(r.id)}
                          title="Eliminar"
                          data-testid={`btn-del-roto-${r.id}`}
                        >
                          <Trash2 className="w-4 h-4 text-destructive" />
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Confirmación de eliminación */}
      <AlertDialog open={!!deletingId} onOpenChange={(o) => !o && setDeletingId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2">
              <AlertCircle className="w-5 h-5 text-destructive" />
              ¿Eliminar este roto?
            </AlertDialogTitle>
            <AlertDialogDescription>
              {deletingRoto ? (
                <>
                  Se devolverán <strong>{(deletingRoto.cantidad || 0).toLocaleString('en-US')}</strong>{' '}
                  <strong>{TIPO_LABELS_CONTEO[deletingRoto.tipo_huevo] || deletingRoto.tipo_huevo}</strong>{' '}
                  {deletingRoto.lote_codigo ? (
                    <>al <strong>{deletingRoto.lote_codigo.toLowerCase().startsWith('lote') ? deletingRoto.lote_codigo : `Lote ${deletingRoto.lote_codigo}`}</strong> y al</>
                  ) : 'al'} inventario disponible. Esta acción no se puede deshacer.
                </>
              ) : 'Esta acción no se puede deshacer.'}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction
              onClick={confirmDelete}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              Eliminar
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};
